'use client';

import { useState, useMemo } from 'react';
import { Bundle, Service } from '@/util/types/serverTypes';
import { Input, Button, Selection } from '@heroui/react';
import { FaMagnifyingGlass, FaArrowLeft } from 'react-icons/fa6';
import { useScheduler } from '../SchedulerContext';
import ServiceConfirmation from './ServiceConfirmation';
import ServiceListbox from './ServiceListbox';

type SearchServicesProps = {
  allServices: Service[];
  bundles: Bundle[];
  onBack: () => void;
  onServiceSelected: () => void;
  showBackButton?: boolean;
};

export default function SearchServices({
  allServices,
  bundles,
  onBack,
  onServiceSelected,
  showBackButton = true,
}: SearchServicesProps) {
  const {
    selectedPrimaryService,
    setSelectedPrimaryService,
    companyStyles,
    scheduleSettings,
  } = useScheduler();
  const [searchQuery, setSearchQuery] = useState('');

  // Filter services based on search query, sort by usage, and limit to 5
  const { filteredServices, totalCount } = useMemo(() => {
    let services = allServices;

    // Filter by search query if provided
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      services = services.filter(
        (service) =>
          service.name.toLowerCase().includes(query) ||
          service.description?.toLowerCase().includes(query)
      );
    }

    // Sort by usage count (most used first), then by name
    // Exclude add-on-only services from primary search (this step selects primary service)
    const sorted = [...services]
      .filter((service) => !service.excludeFromScheduler)
      .filter((service) => service.canBePrimary !== false)
      .sort((a, b) => {
        const usageA = a.usageCount || 0;
        const usageB = b.usageCount || 0;
        if (usageB !== usageA) {
          return usageB - usageA;
        }
        return a.name.localeCompare(b.name);
      });

    // Limit to 5 results
    return {
      filteredServices: sorted.slice(0, 5),
      totalCount: sorted.length,
    };
  }, [allServices, searchQuery]);

  const handleServiceSelect = (keys: Selection) => {
    if (keys === 'all') {
      setSelectedPrimaryService(null);
      return;
    }
    const selected = Array.from(keys as Set<string>)[0];
    setSelectedPrimaryService(selected || null);
  };

  // Get the selected service details
  const selectedService = allServices.find(
    (s) => s._id === selectedPrimaryService
  );

  // Show confirmation if a service is selected
  if (selectedPrimaryService && selectedService) {
    return (
      <ServiceConfirmation
        service={selectedService}
        onChangeSelection={() => setSelectedPrimaryService(null)}
        onNext={onServiceSelected}
        bundles={bundles}
        allServices={allServices}
      />
    );
  }

  return (
    <div className='space-y-6'>
      {/* Back Button - only show if questions exist */}
      {showBackButton && (
        <Button
          variant='flat'
          color='default'
          startContent={<FaArrowLeft />}
          onPress={onBack}
        >
          Back to Questions
        </Button>
      )}

      {/* Header */}
      <div className='text-center'>
        <h2 className='mb-2 text-2xl font-bold text-gray-900'>
          Search for Services
        </h2>
        <p className='text-gray-600'>Find your service by searching below</p>
      </div>

      {/* Search Input */}
      <div className='mx-auto max-w-2xl'>
        <Input
          type='text'
          placeholder='Search by service name...'
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size='lg'
          startContent={<FaMagnifyingGlass className='text-gray-400' />}
          classNames={{
            inputWrapper: 'border bg-white shadow-none',
            input: 'text-base',
          }}
          variant='bordered'
          isClearable
          onClear={() => setSearchQuery('')}
        />
      </div>

      {/* Results */}
      <div>
        {filteredServices.length > 0 ? (
          <div className='mx-auto max-w-2xl'>
            <p className='mb-3 text-sm text-gray-600'>
              Showing {filteredServices.length} of {totalCount} service
              {totalCount !== 1 ? 's' : ''} (sorted by most used)
            </p>
            <ServiceListbox
              services={filteredServices}
              selectedKeys={
                selectedPrimaryService
                  ? new Set([selectedPrimaryService])
                  : new Set()
              }
              onSelectionChange={handleServiceSelect}
              selectionMode='single'
              ariaLabel='Service Search Results'
              iconBackgroundColor={
                companyStyles.companyColor
                  ? `${companyStyles.companyColor}66`
                  : undefined
              }
              iconTextColor={companyStyles.companyColor || undefined}
              showPrice={scheduleSettings?.showPricingInOnlineScheduler}
            />
          </div>
        ) : (
          <div className='mx-auto max-w-2xl text-center'>
            <p className='text-gray-500'>
              {searchQuery.trim()
                ? 'No services found matching your search.'
                : 'Start typing to search for services.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
